import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Flex, Form, Input, Modal, Steps } from "antd";
import Upload from "@/components/Upload";
import templateStore from "@/store/templateStore";
import { GetLocalStorage } from "@/utils/helper";
import PublicPostcard from "@/page/PublicTemplates/Postcard";
import type { ICusTemplate } from "@/store/interface/template";

const FormPostcard = ({ initialData }: { initialData?: ICusTemplate }) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewConfig, setPreviewConfig] = useState<any>({});
  const { saveConfig, updateConfig } = templateStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (initialData) form.setFieldsValue(initialData.config);
  }, []);

  const handlePreview = () => {
    setPreviewConfig(form.getFieldsValue());
    setPreviewOpen(true);
  };

  const steps = [{ title: "Bước 1" }, { title: "Bước 2" }, { title: "Bước 3" }];

  const stepContent = [
    // Step 1 — Thông tin người gửi & người nhận
    <>
      <Form.Item
        name="sender_name"
        label="Người gửi"
        rules={[{ required: true, message: "Vui lòng nhập tên người gửi" }]}
      >
        <Input placeholder="Nhập tên người gửi" />
      </Form.Item>
      <Form.Item name="sender_avatar" label="Ảnh người gửi">
        <Upload maxSize={10} />
      </Form.Item>
      <Form.Item
        name="receiver_name"
        label="Người nhận"
        rules={[{ required: true, message: "Vui lòng nhập tên người nhận" }]}
      >
        <Input placeholder="Nhập tên người nhận" />
      </Form.Item>
      <Form.Item name="location" label="Gửi từ đâu?">
        <Input placeholder="VD: Đà Lạt, Paris, Tokyo..." />
      </Form.Item>
      <Form.Item name="date" label="Ngày gửi">
        <Input placeholder="VD: 25/12/2025" />
      </Form.Item>
    </>,
    // Step 2 — Nội dung bưu thiếp
    <>
      <Form.Item name="stamp_image" label="Ảnh tem (Stamp)">
        <Upload maxSize={10} />
      </Form.Item>
      <Form.Item name="cover_image" label="Ảnh bìa bưu thiếp">
        <Upload maxSize={10} />
      </Form.Item>
      <Form.Item
        name="message"
        label="Nội dung lời nhắn"
        rules={[{ required: true, message: "Vui lòng nhập lời nhắn" }]}
      >
        <Input.TextArea placeholder="Viết lời nhắn lên bưu thiếp..." rows={5} />
      </Form.Item>
      <Form.Item name="quote" label="Câu trích dẫn yêu thích (tuỳ chọn)">
        <Input placeholder="VD: Wish you were here..." />
      </Form.Item>
    </>,
    // Step 3 — Ảnh & Video kỷ niệm
    <>
      <Form.Item name="video" label="Video kỷ niệm">
        <Upload type="video" maxSize={10} />
      </Form.Item>
      <Form.Item name="gallery" label="Bộ ảnh kỷ niệm">
        <Upload multiple maxSize={10} />
      </Form.Item>
    </>,
  ];

  const next = () => setCurrentStep(currentStep + 1);
  const prev = () => setCurrentStep(currentStep - 1);

  const handleFinish = async (value: any) => {
    try {
      let result;
      if (initialData) {
        result = await updateConfig(initialData.xid, { config: { ...value } });
      } else {
        const serial = await GetLocalStorage("scan-storage", "serial");
        result = await saveConfig({ type: "postcard", chip_serial: serial, config: { ...value } });
      }
      if (result) navigate("/");
    } catch (error) {
      console.log("error", error);
    }
  };

  return (
    <section className="container">
      <Steps responsive={false} type="dot" titlePlacement="vertical" current={currentStep} items={steps} />

      <Form form={form} layout="vertical" style={{ marginTop: 24 }} onFinish={handleFinish} preserve>
        {stepContent.map((content, index) => (
          <div key={index} style={{ display: index === currentStep ? "block" : "none" }}>
            {content}
          </div>
        ))}

        <Flex className="mt-3">
          {currentStep > 0 && (
            <Button style={{ marginRight: 8 }} onClick={prev} className="w-full">
              Quay lại
            </Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button type="primary" onClick={next} className="w-full">
              Tiếp theo
            </Button>
          )}
          {currentStep === steps.length - 1 && (
            <>
              <Button onClick={handlePreview} style={{ marginRight: 8 }} className="w-full">
                Xem trước
              </Button>
              <Button type="primary" htmlType="submit" className="w-full">
                Hoàn thành
              </Button>
            </>
          )}
        </Flex>
      </Form>

      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        title="Xem trước"
        width={480}
        style={{ top: 8 }}
        styles={{ body: { padding: 0, height: "calc(100vh - 110px)", overflow: "auto" } }}
      >
        <PublicPostcard config={previewConfig} />
      </Modal>
    </section>
  );
};

export default FormPostcard;
